import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, AlertTriangle } from "lucide-react";
import { useClientsMap, displayName, ClientMapRecord } from "@/api/clients";

/**
 * Módulo "Operación" (a pedido explícito del usuario: "crea un módulo que
 * diga Operación, ahí debe estar un mapa y en el mapa se verá cada cliente y
 * la ubicación, y que si se da clic aparezca si es persona jurídica o
 * natural y sus obligaciones"). El mapa usa OpenStreetMap (Leaflet) — no
 * Google Maps, que exige una clave de API de pago no configurada; con
 * OpenStreetMap el mapa funciona real desde el primer despliegue, sin
 * "Integración no configurada" de por medio.
 *
 * Las coordenadas de cada cliente son manuales (se marcan desde la ficha del
 * cliente, ver EditClientModal.tsx) — no hay ninguna integración de
 * geocodificación automática configurada, así que un cliente solo aparece en
 * el mapa si alguien marcó su ubicación a mano. Es una limitación honesta,
 * no un dato inventado.
 */

const BOGOTA_CENTER: [number, number] = [4.65, -74.1];

const TYPE_COLOR: Record<ClientMapRecord["clientType"], string> = {
  PERSONA_JURIDICA: "#2f5488", // brand-600
  PERSONA_NATURAL: "#c2861f", // accent-500
};

const TYPE_LABEL: Record<ClientMapRecord["clientType"], string> = {
  PERSONA_JURIDICA: "Persona jurídica",
  PERSONA_NATURAL: "Persona natural",
};

export default function OperacionPage() {
  const { data, isLoading } = useClientsMap();
  const clients = data?.clients ?? [];
  const withoutLocation = (data?.totalClients ?? 0) - (data?.withLocation ?? 0);

  const center: [number, number] =
    clients.length > 0 ? [Number(clients[0].latitude), Number(clients[0].longitude)] : BOGOTA_CENTER;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Operación</h1>
        <p className="text-sm text-gray-500">Mapa de clientes: ubicación, tipo y obligaciones, de un vistazo.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700">
          <MapPin size={12} /> {data?.withLocation ?? 0} clientes ubicados en el mapa
        </span>
        {withoutLocation > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
            <AlertTriangle size={12} /> {withoutLocation} clientes sin ubicación registrada — agrégala desde la ficha del cliente
          </span>
        )}
      </div>

      <div className="card overflow-hidden p-0" style={{ height: "70vh" }}>
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">Cargando mapa...</div>
        ) : (
          <MapContainer center={center} zoom={clients.length > 0 ? 11 : 6} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {clients.map((c) => (
              <CircleMarker
                key={c.id}
                center={[Number(c.latitude), Number(c.longitude)]}
                radius={9}
                pathOptions={{ color: TYPE_COLOR[c.clientType], fillColor: TYPE_COLOR[c.clientType], fillOpacity: 0.85, weight: 2 }}
              >
                <Popup>
                  <div className="min-w-[200px] space-y-1.5 text-sm">
                    <p className="font-semibold text-gray-900">
                      <Link to={`/clients/${c.id}`} className="hover:text-brand-600 hover:underline">
                        {displayName(c)}
                      </Link>
                    </p>
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                      style={{ backgroundColor: TYPE_COLOR[c.clientType] }}
                    >
                      {TYPE_LABEL[c.clientType]}
                    </span>
                    {(c.city || c.state) && <p className="text-xs text-gray-500">{[c.city, c.state].filter(Boolean).join(", ")}</p>}
                    <div>
                      <p className="text-xs font-medium text-gray-700">Obligaciones:</p>
                      <p className="text-xs text-gray-600">
                        {c.taxResponsibilities.length > 0 ? c.taxResponsibilities.join(", ") : "Sin obligaciones registradas"}
                      </p>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Azul: persona jurídica · Dorado: persona natural. Haz clic en un punto del mapa para ver el detalle.
      </p>
    </div>
  );
}
